<script>
import Layout from '@layouts/main.vue'
import baseMixin from '@/mixins/baseMixin'
import idleMixin from '@/mixins/idleMixin'
import { mapActions, mapGetters } from 'vuex'
import BookingCreateMeta from '@/components/booking/booking-create-meta.vue'
import BookingLocationList from '@/components/booking/booking-location-list.vue'
import { isEmpty } from 'lodash'
import PromiseDialogOld from '@/components/general/promise-dialog-old.vue'
import BookingCreateSummaryCard from '@/components/booking/booking-create-summary-card.vue'
import dayjs from '@/services/date'
import PromiseConfirmDialog from '@/components/general/promise-confirm-dialog.vue'
import routeDefinitions from '@/shared/constants/routes/routeDefinitions'

export default {
  name: 'BookingCreateView',
  metaInfo() {
    return {
      title: this.$route?.meta?.title,
      meta: [{ name: 'description', content: 'The Booking Create page.' }],
    }
  },
  components: {
    Layout,
    BookingCreateMeta,
    BookingLocationList,
    BookingCreateSummaryCard,
    PromiseDialogOld,
    PromiseConfirmDialog,
  },
  mixins: [baseMixin, idleMixin],
  data: () => {
    return {
      selectedLocation: null,
      formChanged: false,
      editFormChanged: false,
      showSummaryPage: false,
      summaryData: null,
      preselectDate: null,
      isEditMode: false,
      editModeType: null,
      bookingId: null,
      booking: null,
    }
  },
  computed: {
    ...mapGetters({
      locations: 'locations/bookingLocations',
    }),
  },
  watch: {
    idleMixin_isIdle(v, o) {
      this.loadLocationBookingBlocks()
    },
  },
  async created() {
    this.validatePreselectDate()

    this.loadLocationBookingBlocks()
    const result = await this.loadBookingLocations()

    // Auto select location if only one was returned
    if (this.hasSingleLocation(result)) this.selectLocation(result.data[0])

    if (this.$route.name === 'bookings-edit') {
      this.isEditMode = true
      this.editModeType = this.$route.query.mode
      this.bookingId = this.$route.query.bookingId
      this.preselectDate = this.$route.query.date
      await this.loadBookingDetails(this.bookingId)
    }
  },
  async beforeRouteLeave(to, from, next) {
    // Case 1: Edit mode form unsaved
    if (this.isEditMode) {
      if (!this.editFormChanged) {
        next()
      } else {
        const result = await this.$refs.editFormUnsavedChangesDialog.show({
          title: this.$t('general.unsavedChangesDialogConfirmation.title'),
          text: this.$t('general.unsavedChangesDialogConfirmation.desc'),
          icon: 'mdi-alert-outline',
          iconClass: 'icon-warning',
          actions: [
            {
              key: 'cancel',
              text: this.$t('general.cancel'),
              color: 'grey',
              hoverColor: 'grey',
              textBtn: true,
              outlined: true,
              light: true,
              width: '125',
              height: '44',
              borderRadius: '8px',
              event: 'cancel',
              class: 'cancel-btn',
            },
            {
              key: 'discard',
              text: this.$t('general.discardDialogDiscardBtnText'),
              color: 'grey',
              hoverColor: 'grey',
              textBtn: true,
              outlined: true,
              light: true,
              width: '125',
              height: '44',
              borderRadius: '8px',
              event: 'discard',
              class: 'discard-btn',
            },
            {
              key: 'save',
              text: this.$t('bookingEdit.submitBtn'),
              color: 'primary',
              width: '147',
              height: '44',
              borderRadius: '8px',
              event: 'save',
              class: 'save-btn',
            },
          ],
        })

        if (result === 'save') {
          next(false)
          this.$nextTick(() => {
            window.scrollTo({
              top: document.body.scrollHeight,
              behavior: 'smooth',
            })
          })
          this.editFormChanged = false
          await this.$refs.bookingCreateMeta.submit()
        } else if (result === 'discard') {
          this.editFormChanged = false
          next()
        } else {
          next(false)
        }
      }
    } else {
      // Case 2: Create mode unsaved form
      if (!this.formChanged) {
        next()
      } else {
        // Wait for users response to dialog
        const result = await this.$refs.unsavedChangesDialog.show({
          text: this.$t('general.discardDialogBodyText'),
          title: this.$t('general.discardDialogTitle'),
          actions: {
            cancel: this.$t('general.cancel'),
            confirm: {
              text: this.$t('general.discardDialogDiscardBtnText'),
              color: 'red',
            },
          },
          icon: false,
          persistant: true,
        })

        if (result === 'ok') next()
      }
    }
  },
  methods: {
    ...mapActions({
      loadBookingLocations: 'locations/loadBookingLocations',
      loadLocationBookingBlocks: 'locations/loadLocationBookingBlocks',
      setRecentLocation: 'bookings/setRecentLocation',
      getBookingById: 'bookings/getBookingById',
    }),
    async loadBookingDetails(bookingId) {
      const response = await this.getBookingById(bookingId)
      if (response && response.data) {
        this.booking = response.data
        const bookingLocation = this.locations.find(
          (x) => x.locationId === this.booking.location.id
        )
        if (bookingLocation) {
          this.selectLocation(bookingLocation)
        }
      }
    },
    validatePreselectDate() {
      if (
        !this.$route.query ||
        !Object.prototype.hasOwnProperty.call(this.$route.query, 'date')
      )
        return

      const dateQuery = dayjs(this.$route.query.date)
      const isPast = dateQuery.isBefore(dayjs())

      if (isPast) return
      this.preselectDate = this.$route.query.date
    },
    selectLocation(location) {
      this.selectedLocation = location
      this.setRecentLocation(location.locationId)
    },
    changeLocation() {
      this.selectedLocation = null
    },
    hasSingleLocation(resultObj) {
      return (
        resultObj.isSuccess &&
        !isEmpty(resultObj.data) &&
        Array.isArray(resultObj.data) &&
        resultObj.data.length === 1
      )
    },
    handleDisplaySummaryPage(summaryObj) {
      this.formChanged = false
      this.summaryData = summaryObj
      this.showSummaryPage = true
    },
    createNewBooking() {
      this.showSummaryPage = false
      if (this.hasSingleLocation({ isSuccess: true, data: this.locations }))
        this.selectLocation(this.locations[0])
      else this.changeLocation()
      this.summaryData = null
    },
    async handleBackClick() {
      // If edit mode and unsaved changes exist, show dialog
      if (this.isEditMode) {
        if (!this.editFormChanged) {
          this.$router.back()
        } else {
          const result = await this.$refs.editFormUnsavedChangesDialog.show({
            title: this.$t('general.unsavedChangesDialogConfirmation.title'),
            text: this.$t('general.unsavedChangesDialogConfirmation.desc'),
            icon: 'mdi-alert-outline',
            iconClass: 'icon-warning',
            actions: [
              {
                key: 'cancel',
                text: this.$t('general.cancel'),
                color: 'grey',
                hoverColor: 'grey',
                textBtn: true,
                outlined: true,
                light: true,
                width: '125',
                height: '44',
                borderRadius: '8px',
                event: 'cancel',
                class: 'cancel-btn',
              },
              {
                key: 'discard',
                text: this.$t('general.discardDialogDiscardBtnText'),
                color: 'grey',
                hoverColor: 'grey',
                textBtn: true,
                outlined: true,
                light: true,
                width: '125',
                height: '44',
                borderRadius: '8px',
                event: 'discard',
                class: 'discard-btn',
              },
              {
                key: 'save',
                text: this.$t('bookingEdit.submitBtn'),
                color: 'primary',
                width: '147',
                height: '44',
                borderRadius: '8px',
                event: 'save',
                class: 'save-btn',
              },
            ],
          })

          if (result === 'save') {
            this.$nextTick(() => {
              window.scrollTo({
                top: document.body.scrollHeight,
                behavior: 'smooth',
              })
            })
            this.editFormChanged = false
            await this.$refs.bookingCreateMeta.submit()
          } else if (result === 'discard') {
            this.editFormChanged = false
            this.$router.back()
          }
        }
      } else {
        // If create booking and unsaved changes exists, show dialog
        if (!this.formChanged) {
          if (window.history.length > 1) {
            this.$router.back()
          } else {
            this.$router.push({ name: routeDefinitions.home.name })
          }
        } else {
          // Wait for users response to dialog
          const result = await this.$refs.unsavedChangesDialog.show({
            text: this.$t('general.discardDialogBodyText'),
            title: this.$t('general.discardDialogTitle'),
            actions: {
              cancel: this.$t('general.cancel'),
              confirm: {
                text: this.$t('general.discardDialogDiscardBtnText'),
                color: 'red',
              },
            },
            icon: false,
            persistant: true,
          })

          if (result === 'ok') {
            this.formChanged = false
            this.$router.back()
          }
        }
      }
    },
  },
}
</script>

<template>
  <Layout>
    <BookingCreateSummaryCard
      v-if="showSummaryPage"
      :edit-mode="isEditMode"
      :edit-type="editModeType"
      :summary-data="summaryData"
      @createNewBooking="createNewBooking"
    />
    <BookingLocationList
      v-else-if="!selectedLocation"
      @selectLocation="selectLocation"
    />
    <BookingCreateMeta
      v-else
      ref="bookingCreateMeta"
      :selected-location="selectedLocation"
      :preselect-date="preselectDate"
      :edit-mode="isEditMode"
      :edit-type="editModeType"
      :booking="booking"
      @showSummaryPage="handleDisplaySummaryPage"
      @changeLocation="changeLocation"
      @updated="formChanged = true"
      @editChanged="editFormChanged = $event"
      @backClicked="handleBackClick"
    />
    <PromiseDialogOld ref="unsavedChangesDialog" />
    <PromiseConfirmDialog ref="editFormUnsavedChangesDialog" :width="'544'" />
  </Layout>
</template>
